import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { CITY_BY_SLUG } from "@/data/cities";
import { SERVICES } from "@/data/services";
import { Overline } from "@/components/brand/SectionDivider";
import EvaluationForm from "@/components/forms/EvaluationForm";
import { MapPin } from "lucide-react";
import { ConcentricMonogram } from "@/components/brand/ConcentricMonogram";
import { AmbientLight } from "@/components/brand/AmbientLight";
import useDocumentTitle from "@/lib/useDocumentTitle";

export default function CityPage() {
  const { slug } = useParams();
  const city = CITY_BY_SLUG[slug];
  useDocumentTitle(city ? `${city.name} Public Adjusters | AG Public Adjusting` : "Florida Coverage | AG Public Adjusting");
  if (!city) return <Navigate to="/florida-coverage" replace />;
  const featured = SERVICES.slice(0, 6);

  return (
    <main data-testid={`city-page-${slug}`} className="pt-32 bg-midnight-gradient">
      <section className="relative py-24 lg:py-36 overflow-hidden">
        <AmbientLight size={1200} color="blue" intensity={0.55} style={{ top: "-10%", right: "10%" }} />
        <div className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 opacity-40 pointer-events-none" aria-hidden>
          <ConcentricMonogram size={500} variant="ghost" glowColor="blue" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
          <Link to="/florida-coverage" className="overline text-gold/70 hover:text-gold inline-flex items-center gap-2 mb-8">
            ← Florida Coverage
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-4 h-4 text-gold" />
            <span className="overline">{city.region}</span>
          </div>
          <h1 className="font-display-tight text-white text-[clamp(4rem,9vw,9rem)] leading-[0.9] tracking-[-0.05em]">
            <span className="gold-metallic font-display font-light">{city.name}.</span>
          </h1>
          <p className="mt-10 text-ag-text-secondary text-lg lg:text-xl leading-[1.65] font-light max-w-3xl">{city.intro}</p>
        </div>
      </section>

      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline className="mb-8">Local Claim Landscape</Overline>
            <h2 className="font-display-tight text-white text-[clamp(2rem,3.5vw,3rem)] leading-[1.05] tracking-[-0.035em]">
              Claims moving through
              <br />
              <span className="gold-metallic font-display font-light">{city.name} most often.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 flex flex-wrap gap-3">
            {city.common_claims.map((t) => (
              <span key={t} className="glass px-6 py-4 font-display text-lg text-white font-light tracking-[-0.02em]">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 border-t border-white/5 bg-midnight-deeper">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Overline className="mb-8">Services Available in {city.name}</Overline>
          <h2 className="font-display-tight text-white text-[clamp(2rem,4vw,3.5rem)] mb-16 leading-[1.05] tracking-[-0.04em]">
            All 15 services, <span className="gold-metallic font-display font-light">statewide.</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((s) => (
              <Link
                key={s.slug}
                to={`/claim-services/${s.slug}`}
                className="glass block p-8 hover:border-gold/30 transition-all duration-500 group"
                data-testid={`city-${slug}-service-${s.slug}`}
              >
                <h3 className="font-display text-xl text-white group-hover:gold-metallic group-hover:text-transparent transition-colors font-light tracking-[-0.02em]">{s.title}</h3>
                <p className="text-sm text-ag-text-secondary mt-3 font-light line-clamp-2 leading-[1.6]">{s.short}</p>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/claim-services" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.18em] uppercase text-gold hover:text-gold-light">
              View all 15 services →
            </Link>
          </div>
        </div>
      </section>

      <section className="relative py-24 lg:py-32 border-t border-white/5 overflow-hidden">
        <AmbientLight size={900} color="blue" intensity={0.3} style={{ top: "30%", left: "-10%" }} />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Overline className="mb-8">Begin Evaluation</Overline>
            <h2 className="font-display-tight text-white text-[clamp(2rem,4vw,3.5rem)] leading-[1.05] tracking-[-0.04em]">
              Property in <span className="gold-metallic font-display font-light">{city.name}?</span>
            </h2>
            <p className="mt-8 text-ag-text-secondary leading-[1.65] font-light">
              The firm represents policyholders in {city.name} and the broader {city.region}.
            </p>
          </div>
          <div className="lg:col-span-7">
            <EvaluationForm />
          </div>
        </div>
      </section>
    </main>
  );
}
